import React, { useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, ChevronLeftIcon, ChevronRightIcon, TagsIcon } from 'lucide-react';
type CategoryRow = {
  name: string;
  units: string;
  orders: string;
  sales: string;
  aur: string;
  discount: string;
  sellThrough: number;
  color: string;
};
const categoryRows: CategoryRow[] = [{
  name: 'Dresses',
  units: '1,131',
  orders: '900',
  sales: '$128,074',
  aur: '$142',
  discount: '76%',
  sellThrough: 62,
  color: 'bg-[#A77ACA]'
}, {
  name: 'Skirts',
  units: '136',
  orders: '118',
  sales: '$9,714',
  aur: '$82',
  discount: '75%',
  sellThrough: 53,
  color: 'bg-[#A77ACA]'
}, {
  name: 'Blazers',
  units: '82',
  orders: '76',
  sales: '$7,504',
  aur: '$99',
  discount: '78%',
  sellThrough: 49,
  color: 'bg-[#C97968]'
}, {
  name: 'Nightgowns',
  units: '102',
  orders: '85',
  sales: '$3,939',
  aur: '$46',
  discount: '73%',
  sellThrough: 65,
  color: 'bg-[#C9A04E]'
}, {
  name: 'Pants',
  units: '61',
  orders: '58',
  sales: '$3,234',
  aur: '$56',
  discount: '78%',
  sellThrough: 82,
  color: 'bg-[#C99555]'
}, {
  name: 'Shirts',
  units: '55',
  orders: '48',
  sales: '$2,275',
  aur: '$47',
  discount: '79%',
  sellThrough: 42,
  color: 'bg-[#6D9A73]'
}, {
  name: 'Jackets',
  units: '22',
  orders: '22',
  sales: '$2,115',
  aur: '$96',
  discount: '74%',
  sellThrough: 100,
  color: 'bg-[#6D9A73]'
}, {
  name: 'Cardigans',
  units: '32',
  orders: '31',
  sales: '$1,789',
  aur: '$58',
  discount: '81%',
  sellThrough: 97,
  color: 'bg-[#C9A04E]'
}, {
  name: 'Vests',
  units: '22',
  orders: '21',
  sales: '$1,596',
  aur: '$76',
  discount: '77%',
  sellThrough: 31,
  color: 'bg-[#C97968]'
}, {
  name: 'Blouses',
  units: '36',
  orders: '33',
  sales: '$1,480',
  aur: '$45',
  discount: '77%',
  sellThrough: 39,
  color: 'bg-[#6D9A73]'
}, {
  name: 'Bra Top',
  units: '4',
  orders: '4',
  sales: '$103',
  aur: '$26',
  discount: '85%',
  sellThrough: 100,
  color: 'bg-[#6D9A73]'
}, {
  name: 'Shorts',
  units: '1',
  orders: '1',
  sales: '$18',
  aur: '$18',
  discount: '88%',
  sellThrough: 100,
  color: 'bg-[#C9A04E]'
}, {
  name: 'Button Down Shirt',
  units: '5',
  orders: '5',
  sales: '$423',
  aur: '$85',
  discount: '63%',
  sellThrough: 83,
  color: 'bg-[#6D9A73]'
}, {
  name: 'Bag',
  units: '4',
  orders: '4',
  sales: '$155',
  aur: '$39',
  discount: '74%',
  sellThrough: 80,
  color: 'bg-[#C99555]'
}, {
  name: 'Socks',
  units: '16',
  orders: '16',
  sales: '$104',
  aur: '$6',
  discount: '64%',
  sellThrough: 80,
  color: 'bg-[#C9A04E]'
}, {
  name: 'Tank Top',
  units: '39',
  orders: '36',
  sales: '$1,381',
  aur: '$38',
  discount: '80%',
  sellThrough: 74,
  color: 'bg-[#A77ACA]'
}, {
  name: 'Robe',
  units: '44',
  orders: '40',
  sales: '$1,223',
  aur: '$31',
  discount: '74%',
  sellThrough: 68,
  color: 'bg-[#C99555]'
}, {
  name: 'Pajamas',
  units: '25',
  orders: '24',
  sales: '$1,357',
  aur: '$57',
  discount: '70%',
  sellThrough: 64,
  color: 'bg-[#A77ACA]'
}, {
  name: 'Trouser',
  units: '1',
  orders: '1',
  sales: '$30',
  aur: '$30',
  discount: '87%',
  sellThrough: 50,
  color: 'bg-[#6D9A73]'
}, {
  name: 'Crop Top',
  units: '23',
  orders: '23',
  sales: '$1,176',
  aur: '$51',
  discount: '77%',
  sellThrough: 44,
  color: 'bg-[#A77ACA]'
}];
type SortKey = 'name' | 'units' | 'orders' | 'sales' | 'aur' | 'discount' | 'sellThrough';
const PAGE_SIZE = 10;
function metricNumber(value: string) {
  return Number(value.replace(/[^0-9.-]/g, ''));
}
export function CategoryPerformance({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');
  const [page, setPage] = useState(1);
  const hasData = selectedOwner === 'Eddy';
  const sortedRows = useMemo(() => [...categoryRows].sort((left, right) => {
    const leftValue = sortKey === 'name' ? left.name : sortKey === 'sellThrough' ? left.sellThrough : metricNumber(left[sortKey]);
    const rightValue = sortKey === 'name' ? right.name : sortKey === 'sellThrough' ? right.sellThrough : metricNumber(right[sortKey]);
    const comparison = typeof leftValue === 'string' ? leftValue.localeCompare(rightValue as string) : (leftValue as number) - (rightValue as number);
    return sortDirection === 'ascending' ? comparison : -comparison;
  }), [sortDirection, sortKey]);
  const pageCount = Math.ceil(sortedRows.length / PAGE_SIZE);
  const visibleRows = sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const sortRows = (key: SortKey) => {
    if (key === sortKey) setSortDirection(direction => direction === 'ascending' ? 'descending' : 'ascending');else {
      setSortKey(key);
      setSortDirection(key === 'name' ? 'ascending' : 'descending');
    }
    setPage(1);
  };
  const SortHeader = ({
    label,
    sort
  }: {
    label: string;
    sort: SortKey;
  }) => <button type="button" onClick={() => sortRows(sort)} className="inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20" data-id="element-279" data-mp="2fih80dv7hon7">
      {label}
      {sortKey === sort && (sortDirection === 'ascending' ? <ArrowUpIcon className="h-3 w-3" aria-label="Sorted ascending" data-id="element-280" data-mp="25d7st4l3kkba" /> : <ArrowDownIcon className="h-3 w-3" aria-label="Sorted descending" data-id="element-281" data-mp="25belro6kny2r" />)}
    </button>;
  return <section className="mt-5 overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="category-performance-heading" data-id="element-282" data-mp="2aaznu7h1gmjp">
      <div className="flex items-center gap-2 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-283" data-mp="2j5xj3l7nsbe7">
        <TagsIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-284" data-mp="2o3plxa9udhx6" />
        <h2 id="category-performance-heading" className="text-base font-semibold" data-id="element-285" data-mp="2o622bizybk84">Category performance</h2>
      </div>
      <div className="overflow-x-auto" data-id="element-286" data-mp="2y250yevlh0qb">
        {hasData ? <table className="w-full min-w-[900px] text-left text-xs" data-id="element-287" data-mp="2y1wrkwyokdjv">
            <thead className="bg-cream/50 text-ink/50" data-id="element-288" data-mp="yq2in7ypice8">
              <tr data-id="element-289" data-mp="yrnhh9s20hhw">
                <th scope="col" className="px-5 py-3 text-left sm:px-6" data-id="element-290" data-mp="13o47k9itd5xm"><SortHeader label="Category" sort="name" data-id="element-291" data-mp="13onlzg999c6w" /></th>
                <th scope="col" className="px-3 py-3 text-right" data-id="element-292" data-mp="vmwk4jdf0zkb"><SortHeader label="Units sold" sort="units" data-id="element-293" data-mp="vpsi27p9jo8r" /></th>
                <th scope="col" className="px-3 py-3 text-right" data-id="element-294" data-mp="qpwkaq75st1n"><SortHeader label="Order count" sort="orders" data-id="element-295" data-mp="15lvt6323ex7u" /></th>
                <th scope="col" className="px-3 py-3 text-right" data-id="element-296" data-mp="15n6xc3zga6zh"><SortHeader label="Total sales" sort="sales" data-id="element-297" data-mp="10m9d7luvr2vc" /></th>
                <th scope="col" className="px-3 py-3 text-right" data-id="element-298" data-mp="10p5dhgu7a3my"><SortHeader label="AUR" sort="aur" data-id="element-299" data-mp="1elij9spdggll" /></th>
                <th scope="col" className="px-3 py-3 text-right" data-id="element-300" data-mp="19nh0l8vuyjan"><SortHeader label="Avg discount" sort="discount" data-id="element-301" data-mp="19mpfwt3su5z5" /></th>
                <th scope="col" className="px-5 py-3 text-left sm:px-6" data-id="element-302" data-mp="1ocwp0i7x2cn6"><SortHeader label="Sell through" sort="sellThrough" data-id="element-303" data-mp="1obloqsdojn0t" /></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10" data-id="element-304" data-mp="1jfmrefadiuo9">
              {visibleRows.map(category => <tr key={category.name} data-id="element-305" data-mp="1xdbezuvtsfx0">
                  <th scope="row" className="whitespace-nowrap px-5 py-3 text-left font-medium sm:px-6" data-id="element-306" data-mp="1tbzvm73joqx9">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold text-cream ${category.color}`} data-id="element-307" data-mp="2bz8m0urs1bhc">{category.name}</span>
                  </th>
                  <td className="px-3 py-3 text-right tabular-nums text-ink/70" data-id="element-308" data-mp="2gwh6j20edrlr">{category.units}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-ink/70" data-id="element-309" data-mp="27y59ah50zvkc">{category.orders}</td>
                  <td className="px-3 py-3 text-right font-semibold tabular-nums text-ink" data-id="element-310" data-mp="1t995wxdwz6q2">{category.sales}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-ink/70" data-id="element-311" data-mp="1xzpk4jpk6wdh">{category.aur}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-ink/70" data-id="element-312" data-mp="1k8uhhp5nlbic">{category.discount}</td>
                  <td className="px-5 py-3 sm:px-6" data-id="element-313" data-mp="1o9o48f7d0k9n">
                    <div className="flex min-w-32 items-center gap-2" data-id="element-314" data-mp="1oafowuzf4xl5">
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-ink/10" data-id="element-315" data-mp="1abps7ggigzxb">
                        <div className="h-full rounded-full bg-[#BB5E40]" style={{
                    width: `${category.sellThrough}%`
                  }} data-id="element-316" data-mp="1f8g6by8r9lg3" />
                      </div>
                      <span className="w-9 text-right tabular-nums text-ink/55" data-id="element-317" data-mp="1ps69fo835iwx">{category.sellThrough}%</span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table> : <p className="px-6 py-10 text-center text-sm text-ink/55" data-id="element-318" data-mp="1gsj5ovrvw10p">Select an owner to view category performance.</p>}
      </div>
      {hasData && <div className="flex flex-col gap-3 border-t border-ink/10 px-5 py-3 text-xs text-ink/55 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-319" data-mp="1bty5uxgjnjcs">
          <span data-id="element-320" data-mp="12226zk6mvp7i">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sortedRows.length)} of {sortedRows.length} categories</span>
          <div className="flex items-center gap-2 self-end sm:self-auto" data-id="element-321" data-mp="y33382wn1yhn">
            <button type="button" onClick={() => setPage(current => Math.max(1, current - 1))} disabled={page === 1} aria-label="Previous category page" className="rounded-md p-1 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-35" data-id="element-322" data-mp="t0u8b107gmcw"><ChevronLeftIcon className="h-4 w-4" data-id="element-323" data-mp="2zsiglutvc3d1" /></button>
            <span data-id="element-324" data-mp="34ofxq3cyhdqv"><strong className="font-semibold text-ink" data-id="element-325" data-mp="34o7ii4td1wci">{page}</strong> of {pageCount}</span>
            <button type="button" onClick={() => setPage(current => Math.min(pageCount, current + 1))} disabled={page === pageCount} aria-label="Next category page" className="rounded-md p-1 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-35" data-id="element-326" data-mp="39pn3xxzga4vf"><ChevronRightIcon className="h-4 w-4" data-id="element-327" data-mp="2gxtqufeotzig" /></button>
          </div>
        </div>}
    </section>;
}